import React, { useMemo } from "react";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";

import MapaComponent from "./MapaComponent";
import "./MapasCard.scss";
import { formatearHoraAmPm } from "../../../../utilidades/formateos";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";

/* ─── Helpers ─── */
/**
 * Determina si una estructura es de Entrada o Salida a partir de su nombreTurno
 * (ej. "E1" → entrada, "S1" → salida). Se usa cuando la estructura aún no tiene trazo.
 */
const tipoPorNombreTurno = (estructura) => {
    const letra = estructura?.nombreTurno?.trim()?.charAt(0)?.toUpperCase();
    return letra === "S" ? "S" : "E";
};

const agruparTrazosPorTipo = (estructuras = []) => {
    const entrada = [], salida = [];
    estructuras.forEach((est) => {
        const trazos = est.trazos || [];

        // Aún no tiene recorrido creado: se agrupa igual, usando el turno para saber el tipo
        if (trazos.length === 0) {
            const item = { estructura: est, trazo: null };
            if (tipoPorNombreTurno(est) === "S") salida.push(item);
            else entrada.push(item);
            return;
        }

        trazos.forEach((trazo) => {
            const item = { estructura: est, trazo };
            if (trazo.tipo === "E") entrada.push(item);
            else if (trazo.tipo === "S") salida.push(item);
        });
    });

    return { entrada, salida };
};

/**
 * Distancia máxima (metros) para considerar que dos paradas son la misma.
 * Al editar turnos por separado el marcador casi nunca queda en el mismo punto exacto.
 * Debe coincidir con TOLERANCIA_PARADA_METROS y el criterio de nombre del backend (validarTrazosIguales).
 */
const TOLERANCIA_PARADA_METROS = 50;

const distanciaMetros = (a, b) => {
    const lat1 = Number(a.lat), lat2 = Number(b.lat);
    const dLat = (lat2 - lat1) * 111320;
    const dLon = (Number(b.lon) - Number(a.lon)) * 111320 * Math.cos(((lat1 + lat2) / 2) * Math.PI / 180);
    return Math.hypot(dLat, dLon);
};

const ordenarParadas = (trazo) => [...(trazo?.paradas ?? [])].sort((a, b) => a.orden - b.orden);

const normalizarNombre = (nombre) =>
    String(nombre ?? "").trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\s+/g, " ");

/**
 * Dos paradas son la misma si están a menos de TOLERANCIA_PARADA_METROS o si tienen
 * el mismo nombre (rutas de producción con turnos trazados por separado suelen tener
 * el marcador de la misma parada en puntos distintos).
 */
const mismaParada = (a, b) =>
    distanciaMetros(a, b) <= TOLERANCIA_PARADA_METROS ||
    (normalizarNombre(a.nombre) !== "" && normalizarNombre(a.nombre) === normalizarNombre(b.nombre));

/**
 * Dos trazos tienen las mismas paradas si tienen el mismo número, en el mismo orden,
 * y cada una es la misma parada que su par (ver mismaParada).
 */
const mismasParadas = (trazoA, trazoB) => {
    const a = ordenarParadas(trazoA);
    const b = ordenarParadas(trazoB);
    return a.length === b.length && a.every((p, i) => mismaParada(p, b[i]));
};

const itemsConParadas = (items = []) =>
    items.filter((item) => item.estructura?.activa && item.trazo?.paradas?.length);

/**
 * Devuelve true si todos los trazos tienen las mismas paradas. La edición general
 * empareja los horarios por orden de parada, así que solo se permite en ese caso.
 * Las estructuras sin trazo o sin paradas no se comparan (la edición general las llena).
 */
const paradasSonIgualesEnTodos = (items = []) => {
    const conParadas = itemsConParadas(items);
    return conParadas.every((item) => mismasParadas(item.trazo, conParadas[0].trazo));
};

/**
 * Agrupa las estructuras activas con trazo según sus paradas: las que tienen
 * las mismas paradas quedan en el mismo grupo (una sola tabla).
 * Se compara contra el primer trazo de cada grupo y se conserva el orden en que aparecen.
 */
const agruparPorParadas = (items = []) => {
    const grupos = [];
    itemsConParadas(items).forEach((item) => {
        const grupo = grupos.find((g) => mismasParadas(g[0].trazo, item.trazo));
        if (grupo) grupo.push(item);
        else grupos.push([item]);
    });
    return grupos;
};

/**
 * Turnos (por nombre) que aparecen con más de un tipo (ej. "E1" Sencillo y "E1" Medio).
 * Se calcula sobre todas las estructuras del bloque, no solo las de una tabla,
 * para que también se marque cuando se muestra una tabla por turno.
 */
const turnosConVariosTipos = (items = []) => {
    const tiposPorTurno = {};
    (Array.isArray(items) ? items : [])
        .filter(item => item.estructura?.activa)
        .forEach(({ estructura }) => {
            const turno = estructura.nombreTurno?.trim();
            if (!tiposPorTurno[turno]) tiposPorTurno[turno] = new Set();
            tiposPorTurno[turno].add(estructura.nombreTipo?.trim() ?? '');
        });

    return new Set(
        Object.keys(tiposPorTurno).filter(turno => tiposPorTurno[turno].size > 1)
    );
};

/**
 * Construye matriz pivotada de horarios por turno usando nombreTurno como header
 * @param {Array} data
 * @param {Set<string>} [turnosRepetidos] - Turnos que llevan el tipo en el encabezado;
 *        si no se pasa se calcula con `data`
 * @returns {{
 *   rows: Array,
 *   turnosMeta: Array<{ id: number|string, nombreTurno: string }>
 * }}
 */
export const buildScheduleMatrix = (data, turnosRepetidos = turnosConVariosTipos(data)) => {
    if (!Array.isArray(data)) {
        return { rows: [], turnosMeta: [] };
    }

    // 1️⃣ Filtrar activas
    const activas = data.filter(item => item.estructura?.activa);

    // 2️⃣ Construir metadata de turnos — si el mismo turno tiene varios tipos (ej. dos
    //    estructuras "E1"), se agrega el tipo (Sencillo/Medio) para no confundirlos
    const turnosMeta = activas.map(item => {
        const turno = item.estructura.nombreTurno?.trim();
        const tipo  = item.estructura.nombreTipo?.trim();
        return {
            id: item.estructura.id,
            nombreTurno: turnosRepetidos.has(turno) && tipo ? `${turno} · ${tipo}` : turno,
        };
    });

    // 3️⃣ Obtener paradas únicas por orden
    const stopsMap = new Map();

    activas.forEach(item => {
        item.trazo?.paradas?.forEach(parada => {
            if (!stopsMap.has(parada.orden)) {
                stopsMap.set(parada.orden, {
                    orden: parada.orden,
                    nombre: parada.nombre,
                    referencia: parada.referencia
                });
            }
        });
    });

    const allStops = Array.from(stopsMap.values())
        .sort((a, b) => a.orden - b.orden);

    // 4️⃣ Construir filas pivotadas
    const rows = allStops.map(stop => {
        const row = {
            orden: stop.orden,
            nombre: stop.nombre,
            referencia: stop.referencia
        };

        turnosMeta.forEach(turno => {
            const estructura = activas.find(
                item => item.estructura.id === turno.id
            );

            const parada = estructura?.trazo?.paradas?.find(
                p => p.orden === stop.orden
            );

            row[`turno_${turno.id}`] = parada?.horario || "";
        });

        return row;
    });

    return { rows, turnosMeta };
};

/* ─── BarraProgreso ─── */
const BarraProgreso = ({ hayEntrada, haySalida, tieneEntrada, tieneSalida, onValidarTrazo, puedeVerificarTrazos }) => {
    const total  = (tieneEntrada ? 1 : 0) + (tieneSalida ? 1 : 0);
    const listos = (tieneEntrada && hayEntrada ? 1 : 0) + (tieneSalida && haySalida ? 1 : 0);
    const completo = listos === total;

    const color = completo ? "#1B7A3D" : listos > 0 ? "#E65100" : "#B71C1C";

    const texto = completo
        ? "✓ Recorridos listos — puedes continuar"
        : tieneEntrada && tieneSalida
            ? listos === 1 ? "Falta un recorrido" : "Registra los recorridos para continuar"
            : "Registra el recorrido para continuar";

    return (
        <div className="flex flex-wrap gap-2 mb-3">
            <div className="barra-progreso" style={{ borderColor: color }}>
                <div className="barra-progreso__fila">
                    <div className="barra-progreso__track">
                        <div
                            className="barra-progreso__fill"
                            style={{ width: `${(listos / total) * 100}%`, background: color }}
                        />
                    </div>
                    <span className="barra-progreso__num" style={{ color }}>{listos}/{total}</span>
                    <span className="barra-progreso__texto" style={{ color }}>{texto}</span>
                </div>
            </div>
            <div className="flex align-items-center gap-3 ml-auto">
                {completo && puedeVerificarTrazos && (
                    <Button onClick={onValidarTrazo}>
                        Verificar trazos y mandar a costeo
                    </Button>
                )}
            </div>
        </div>
    );
};

/* ─── HorariosCompactos ─── */
const HorariosCompactos = ({ items, esEntrada, visibilidad, tipo, onEditarTrazo }) => {
    // Solo tiene sentido listar horarios de estructuras que ya tienen un recorrido creado
    const itemsConTrazo = (items || []).filter((i) => i.trazo);
    if (itemsConTrazo.length === 0) return null;

    const itemsOrdenados = [...itemsConTrazo].sort((a, b) => {
        const horaA = esEntrada ? a.estructura.horaFin : a.estructura.horaInicio;
        const horaB = esEntrada ? b.estructura.horaFin : b.estructura.horaInicio;
        return horaA.localeCompare(horaB);
    });

    // Turnos que se repiten en este bloque: se marca el tipo (Sencillo/Medio)
    // para que no se confundan al editar el trazo equivocado
    const conteoTurnos = itemsOrdenados.reduce((acc, { estructura }) => {
        acc[estructura.nombreTurno] = (acc[estructura.nombreTurno] || 0) + 1;
        return acc;
    }, {});

    return (
        <div className="bloque__horarios">
            <span className="bloque__seccion-label">
                <i className="pi pi-clock" /> Horarios
            </span>
            {itemsOrdenados.map(({ estructura, trazo }) => {
                const turnoRepetido = conteoTurnos[estructura.nombreTurno] > 1;

                return (
                    <div key={`${estructura.id}-${trazo.id}`} className="horario-fila">
                        <span className="horario-fila__hora">
                            {formatearHoraAmPm(esEntrada ? estructura.horaFin : estructura.horaInicio)}
                        </span>
                        <span className="horario-fila__nombre">
                            {trazo.nombre || `Estructura ${estructura.id}`}
                        </span>
                        {turnoRepetido && (
                            <Tag
                                value={`Turno ${estructura.nombreTurno} · ${estructura.nombreTipo}`}
                                severity="warning"
                                className="horario-fila__tag"
                            />
                        )}
                        {visibilidad.kilometraje && trazo.kilometros > 0 && (
                            <>
                                <Tag value={`${trazo.kilometros} km`} className="horario-fila__tag" />
                                {visibilidad.puedeEditarTrazo && (
                                    <Button onClick={() => {
                                        onEditarTrazo?.(tipo, trazo.id)
                                    }}>Editar trazo</Button>
                                )}
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const TablaHorarios = ({ data, todos = data }) => {

    // `todos` son las estructuras de todo el bloque: decide qué turnos se repiten
    const { rows, turnosMeta } = useMemo(() => {
        return buildScheduleMatrix(data, turnosConVariosTipos(todos));
    }, [data, todos]);

    const dateBodyTemplate = (rowData, options) => {
        const value = rowData[options.field];
        if (!value) return null;

        return <span className="hora-celda">{formatearHoraAmPm(value)}</span>;
    };

    return (
        <DataTable
            value={rows}
            stripedRows
            responsiveLayout="scroll"
            className="p-datatable-sm m-3"
            showGridlines
        >
            <Column field="nombre" header="Parada" />
            <Column field="referencia" header="Referencia" />

            {turnosMeta.map(turno => (
                <Column
                    key={turno.id}
                    field={`turno_${turno.id}`}
                    body={dateBodyTemplate}
                    header={turno.nombreTurno}
                />
            ))}
        </DataTable>
    );
};

/* ─── BloqueRecorrido ─── */
const CONF = {
    E: {
        titulo: "Ida",
        desc: "Del origen a la empresa del cliente",
        color: "#1B7A3D", bg: "#E8F5EC", border: "#B7DFC5",
        btnCrear: "Crear recorrido de Ida",
        btnEditar: "Modificar recorrido de Ida",
    },
    S: {
        titulo: "Regreso",
        desc: "De la empresa de vuelta al origen",
        color: "#1565A6", bg: "#E3F0FA", border: "#B3D4F0",
        btnCrear: "Crear recorrido de Regreso",
        btnEditar: "Modificar recorrido de Regreso",
    },
};

const BloqueRecorrido = ({ numero, totalPasos, tipo, items, bloqueado, visibilidad, puedeGenerarTrazos, onGenerarTrazo, handleEditarAllTrazos, onEditarTrazo }) => {
    const cfg = CONF[tipo];
    const esEntrada    = tipo === "E";
    const completado   = items.length > 0 && items.some((i) => i.trazo?.polyline);
    const activo       = !completado && !bloqueado;
    const primerTrazo  = items[0]?.trazo ?? null;
    const todasParadas = items.flatMap((i) => i.trazo?.paradas || []);
    const hayMapa      = primerTrazo?.polyline && visibilidad.mapa;
    const paradasIguales = paradasSonIgualesEnTodos(items);

    // ----Trazo con mayor kilometraje para edición --------------
    const trazoConMayorKm = items.reduce((max, item) => {
        const km = item.trazo?.kilometros ?? 0;
        return km > (max.trazo?.kilometros ?? 0) ? item : max;
    }, items[0]);

    const trazoParaEditar = trazoConMayorKm?.trazo ?? primerTrazo;

    const claseBloque = [
        "bloque",
        completado ? "bloque--ok"        : "",
        activo     ? "bloque--activo"    : "",
        bloqueado  ? "bloque--bloqueado" : "",
    ].filter(Boolean).join(" ");

    return (
        <div
            className={claseBloque}
            style={!bloqueado ? { borderColor: cfg.border } : undefined}
            aria-disabled={bloqueado}
        >
            {/* ── Encabezado compacto ── */}
            <div
                className="bloque__header"
                style={!bloqueado ? { background: cfg.bg } : undefined}
            >
                <div
                    className={[
                        "bloque__num",
                        completado ? "bloque__num--ok"     : "",
                        activo     ? "bloque__num--activo" : "",
                        bloqueado  ? "bloque__num--bloq"   : "",
                    ].filter(Boolean).join(" ")}
                >
                    {completado ? <i className="pi pi-check" /> : numero}
                </div>

                <div className="bloque__header-textos">
                    <span className="bloque__paso-label">PASO {numero} DE {totalPasos}</span>
                    <h4
                        className="bloque__titulo"
                        style={{ color: bloqueado ? "#9E9E9E" : cfg.color }}
                    >
                        Recorrido de {cfg.titulo}
                    </h4>
                    <p className="bloque__desc">{cfg.desc}</p>
                </div>

                {completado && (
                    <span className="badge badge--ok"><i className="pi pi-check-circle" /> Listo</span>
                )}
                {activo && (
                    <span className="badge badge--pendiente"><i className="pi pi-circle" /> Pendiente</span>
                )}
                {bloqueado && (
                    <span className="badge badge--bloq"><i className="pi pi-lock" /> Bloqueado</span>
                )}
            </div>

            {/* ── Cuerpo ── */}
            <div className="bloque__cuerpo">

                {bloqueado && (
                    <div className="bloque__bloqueado">
                        <i className="pi pi-lock" />
                        <div>
                            <strong>Primero termina el Paso 1 (Recorrido de Ida)</strong>
                            <span>Este paso se habilitará automáticamente cuando el de Ida esté listo.</span>
                        </div>
                    </div>
                )}

                {!bloqueado && hayMapa && (
                    <div className="bloque__mapa">
                        <MapaComponent
                            polylineBase64={trazoParaEditar.polyline}
                            paradas={todasParadas}
                            mensajeLetrero={trazoParaEditar.mensajeLetrero}
                        />
                    </div>
                )}

                {!bloqueado && !hayMapa && visibilidad.trazos && (
                    <div className="bloque__sin-trazo">
                        <i className="pi pi-map" />
                        <p>El mapa aparecerá aquí cuando registres el recorrido.</p>
                    </div>
                )}

                {!bloqueado && visibilidad.puedeEditarTrazo && paradasIguales && (
                    <div className="bloque__accion">
                        {puedeGenerarTrazos ? (
                            <>
                                <Button
                                    label={completado ? cfg.btnEditar : cfg.btnCrear}
                                    icon={completado ? "pi pi-pencil" : "pi pi-plus-circle"}
                                    className="bloque__btn"
                                    style={!completado
                                        ? { background: cfg.color, borderColor: cfg.color, color: "#fff" }
                                        : undefined}
                                    outlined={completado}
                                    onClick={() => completado
                                        ? handleEditarAllTrazos?.(tipo, trazoParaEditar?.id)
                                        : onGenerarTrazo?.(tipo)}
                                />
                                {!completado && (
                                    <span className="bloque__accion-nota">
                                        <i className="pi pi-info-circle" />
                                        Obligatorio para continuar
                                    </span>
                                )}
                            </>
                        ) : (
                            <span className="bloque__accion-nota">
                                <i className="pi pi-lock" />
                                Confirma los datos de equipamiento y horarios para poder generar este recorrido
                            </span>
                        )}
                    </div>
                )}

                {!bloqueado && visibilidad.puedeEditarTrazo && completado && !paradasIguales && (
                    <div className="bloque__accion">
                        <span className="bloque__accion-nota">
                            <i className="pi pi-info-circle" />
                            Los turnos tienen paradas distintas, por eso no se pueden modificar todos juntos.
                            Edita cada turno por separado con "Editar trazo".
                        </span>
                    </div>
                )}

                {!bloqueado && (
                    <HorariosCompactos items={items} esEntrada={esEntrada} visibilidad={visibilidad} tipo={tipo} onEditarTrazo={onEditarTrazo} />
                )}

                {/* Una tabla por grupo de turnos con las mismas paradas: juntar turnos con
                    paradas distintas mezclaría nombres de un turno con horarios de otro */}
                {!bloqueado && visibilidad.paradas && todasParadas.length > 0 && (
                    agruparPorParadas(items).map((grupo) => (
                        <TablaHorarios
                            key={grupo.map((i) => i.estructura.id).join("-")}
                            data={grupo}
                            todos={items}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

/* ─── MapasCard — principal ─── */
const MapasCard = ({ ruta, visibilidad, totalSinCambios = 0, puedeVerificarTrazos = false, puedeGenerarTrazos = true, onGenerarTrazo, handleEditarAllTrazos, onEditarTrazo, onValidarTrazo }) => {
    const { entrada, salida } = useMemo(
        () => agruparTrazosPorTipo(ruta.estructuras),
        [ruta.estructuras]
    );

    const hayEntrada = entrada.some((i) => i.trazo?.polyline);
    const haySalida  = salida.some((i) => i.trazo?.polyline);

    // Un bloque solo existe si la ruta tiene estructuras de ese tipo.
    // Esto permite registrar solo Ida, solo Regreso, o ambos.
    const tieneEntrada = entrada.length > 0;
    const tieneSalida  = salida.length > 0;

    if (!visibilidad.trazos && !visibilidad.puedeEditarTrazo) return null;

    const ambos = tieneEntrada && tieneSalida;
    const totalPasos = ambos ? 2 : 1;


    const sinEstructuras = !ruta.estructuras?.length;
    if (sinEstructuras) {
        return puedeVerificarTrazos && visibilidad.puedeEditarTrazo ? (
            <div className="mapas-ruta">
                <div className="flex justify-content-end mt-2">
                    <Button label="Mandar a costeo" onClick={onValidarTrazo} />
                </div>
            </div>
        ) : null;
    }


    return (
        <div className="mapas-ruta">
            {visibilidad.puedeEditarTrazo && (
                <BarraProgreso
                    hayEntrada={hayEntrada}
                    haySalida={haySalida}
                    tieneEntrada={tieneEntrada}
                    tieneSalida={tieneSalida}
                    onValidarTrazo={onValidarTrazo}
                    puedeVerificarTrazos={puedeVerificarTrazos}
                />
            )}

            {/* En modificaciones solo llegan las estructuras seleccionadas; las demás conservan su recorrido */}
            {totalSinCambios > 0 && (
                <div className="mapas-ruta__aviso-sin-cambios">
                    <i className="pi pi-info-circle" />
                    <span>
                        Solo se muestran los recorridos de las estructuras seleccionadas, que son los únicos que se
                        pueden modificar.{" "}
                        {totalSinCambios === 1
                            ? "El recorrido de la otra estructura se conserva igual en la ruta."
                            : `Los recorridos de las otras ${totalSinCambios} estructuras se conservan igual en la ruta.`}
                    </span>
                </div>
            )}

            <div className="grid">
                {tieneEntrada && (
                    <div className={ambos ? 'col-12 md:col-6' : 'col-12'}>
                        <BloqueRecorrido
                            numero={1} totalPasos={totalPasos} tipo="E" items={entrada}
                            bloqueado={false} visibilidad={visibilidad} puedeGenerarTrazos={puedeGenerarTrazos}
                            onGenerarTrazo={onGenerarTrazo} handleEditarAllTrazos={handleEditarAllTrazos}
                            onEditarTrazo={onEditarTrazo}
                        />
                    </div>
                )}
                {tieneSalida && (
                    <div className={ambos ? 'col-12 md:col-6' : 'col-12'}>
                        <BloqueRecorrido
                            numero={tieneEntrada ? 2 : 1} totalPasos={totalPasos} tipo="S" items={salida}
                            bloqueado={false} visibilidad={visibilidad} puedeGenerarTrazos={puedeGenerarTrazos}
                            onGenerarTrazo={onGenerarTrazo} handleEditarAllTrazos={handleEditarAllTrazos}
                            onEditarTrazo={onEditarTrazo}
                        />
                    </div>
                )}
            </div>

            {((!tieneEntrada || hayEntrada) && (!tieneSalida || haySalida)) && visibilidad.puedeEditarTrazo && (
                <div className="mapas-ruta__listos">
                    <i className="pi pi-check-circle" />
                    <strong>¡{tieneEntrada && tieneSalida ? 'Ambos recorridos están' : 'El recorrido está'} listo!</strong>
                    <span>Ya puedes verificar los datos y continuar al siguiente paso.</span>
                </div>
            )}
        </div>
    );
};

export default MapasCard;
